/** @jsxImportSource @emotion/react */
import React from "react";
import {css} from "@emotion/react";
import Drawer, {drawerContentWithFooterStyle} from "js/common/views/drawer";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import TabbedKpiEditor from "js/admin/kpis/edit-kpis/tabs/tabbed-kpi-editor";
import Immutable from "immutable";
import currentClient from "js/common/repo/backbone/current-client";
import {getQueryType, isKpiEditable, useCalculatedImmutableState, getGroupingEntity} from "js/admin/kpis/edit-kpis/utils";
import * as Styles from "js/common/views/filter-drawer/filters-drawer.styles";
import {TextButton} from "js/common/views/inputs/buttons";
import KpiDrawerMetricTabs from "js/admin/kpis/edit-kpis/common/kpi-drawer-metric-tabs";
import KpiActions from "js/admin/kpis/edit-kpis/common/kpi-actions";
import useDimensions from "js/common/utils/use-dimensions";

const KpiDrawer = ({
  open,
  onClose,
  focusedTabKpiId,
  idToWrappedKpi,
  entityTypeToLabel,
  kpiIdToDependentKpis,
  kpiIdToContributingKpis,
  idToTemplate,
  idToEntityColumn,
  typeToGroupingEntity,
  kpiIdToMasterKpis,
  masterKpiTypeToKpiId,
  isCube19User,
  clientHasEdit,
  isEditor,
  actionTypes,
  onKpiChange,
  onColumnsChange,
  onUnmirror,
  onNavigationClick,
  onTestConfigChange,
  onTest,
  onSave,
  onDiscardChanges,
  onClearRequiresExplanationUpdate,
  onChangeMetricTab,
  onCloseMetricTab,
  onOpenMetricTab,
  onAddMetricClick,
  onLoadUnsavedChangesClick,
  handleDuplicateClick,
  handleInheritClick,
  onRevertToLegacyFormat,
  onDisableMetricToggle,
  onDeleteMetric,
  changedMetricsCount,
  unOpenedChangedMetricsCount,
  reloadDataForKpi,
  onChangeTab,
  sumDoubleSplitsAvailable,
  sumSplitAvailable,
  aggregationTypes,
  trendTypes,
  openedKpiIds,
  nameToCount,
  hasValidationErrors
}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const [ref, {height}] = useDimensions();

  const focusedWrappedKpi = idToWrappedKpi.get(focusedTabKpiId);

  const kpiName = focusedWrappedKpi.getIn(["kpi", "name"]);
  const kpi = focusedWrappedKpi.get("kpi");
  const kpiId = kpi.get("id");
  const combinedKpi = focusedWrappedKpi.get("combinedKpi");
  const testConfig = focusedWrappedKpi.get("testConfig");
  const testResult = focusedWrappedKpi.get("testResult");
  const columnsKpiId = (kpi.get("columnsKpiId") || kpiId);
  const columns = idToWrappedKpi.getIn([columnsKpiId, "columns"]);
  const submitError = focusedWrappedKpi.get("submitError");
  const combineError = focusedWrappedKpi.get("combineError");
  const requiresExplanationUpdate = focusedWrappedKpi.get("requiresExplanationUpdate");
  const tabId = focusedWrappedKpi.get("tabId");
  const rootChanged = focusedWrappedKpi.get("rootChanged");
  const availableOwners = focusedWrappedKpi.get("availableOwners");
  const availableServerColumns = focusedWrappedKpi.get("availableServerColumns");
  const entityType = getGroupingEntity(focusedWrappedKpi);

  // Search will be replaced with tab search once we implement that
  const filterText = "";
  const searchFocused = false;

  const combineWithKpiId = kpi.get("combineWithKpiId");
  const combineWithMasterMetricType = kpi.get("combineWithMasterMetricType");
  const parentKpiId = (combineWithKpiId || masterKpiTypeToKpiId?.get(combineWithMasterMetricType));
  const parentWrappedKpi = parentKpiId ? idToWrappedKpi.get(parentKpiId) : null;
  const parentCombinedKpi = parentWrappedKpi ? parentWrappedKpi.get("combinedKpi") : null;
  const legacyConfigMessage = "This metric cannot be edited from this page. Reach out to support for any changes that are needed.";
  const inheritAndDuplicateDisabled = !isCube19User && !isKpiEditable(kpi, idToTemplate);

  // we want to make sure columns and owner data is loaded when we change tabs
  React.useEffect(() => {
    reloadDataForKpi(kpiId);
  }, [kpiId]);

  const handleTestConfigChange = React.useCallback(
      (testConfig) => {
        onTestConfigChange(kpi.get("id"), testConfig);
      },
      [onTestConfigChange, kpi]);

  const handleTest = React.useCallback(
      () => {
        onTest(focusedTabKpiId);
      },
      [onTest, focusedTabKpiId]);

  const allDependentKpis = kpiIdToDependentKpis.get(kpi.get("id"), Immutable.List());

  const dependencyIds = useCalculatedImmutableState(
      () => allDependentKpis.map(kpi => kpi.get("id")),
      [kpi, allDependentKpis]);

  const handleKpiChange = React.useCallback(
      kpi => {
        onKpiChange(kpi, dependencyIds);
      },
      [onKpiChange, dependencyIds]);

  const kpisWithMatchingRootEntity =
      idToWrappedKpi
          .toList()
          .filter(wk => wk.getIn(["kpi", "readOnlyRootGroupingEntity"]) === kpi.get("readOnlyRootGroupingEntity"))
          .map(wk => wk.get("kpi"));

  const kpisWithMatchingColumns = columnsKpiId ?
      idToWrappedKpi
          .toList()
          .filter(wk =>
              wk.getIn(["kpi", "columnsKpiId"]) === columnsKpiId ||
              wk.getIn(["kpi", "id"]) === columnsKpiId
          )
          .map(wk => wk.get("kpi")) :
      Immutable.List();

  const kpisThatShareColumnsWithThisKpi = kpisWithMatchingColumns.filter(k => k.get("id") !== kpi.get("id") && !k.get(
      "deleted"));

  const queryType = getQueryType(kpi, idToTemplate);

  const columnsQueryType = kpi.get("readOnlyColumnsQueryType") ?? queryType;

  const hasNewConfig = !!kpi.get("config");
  const isLockedByOnboarding = kpi.get("createdByOnboarding")
      && currentClient.getType() !== "ENTERPRISE"
      && currentClient.getImplementationStatus() === "ONBOARDING";

  const hasDependencies = !kpiIdToDependentKpis.get(kpi.get("id"), Immutable.List()).isEmpty();

  const isDependent = !kpiIdToContributingKpis.get(kpi.get("id"), Immutable.List()).isEmpty();

  const hasRelationships = hasDependencies || isDependent;

  const kpiIsCombinationParent = !kpiIdToDependentKpis
      .get(kpi.get("id"), Immutable.List())
      .filter(dependentKpi => dependentKpi.get("dependencyType") === "COMBINATION")
      .isEmpty();

  const masterMetricsAvailableToCombineWith = kpisWithMatchingRootEntity
      .filter(k => !isNaN(k.get("id")) && k.get("id") !== kpi.get("id"))
      .flatMap(kpi => kpiIdToMasterKpis?.get(kpi.get("id"), Immutable.List()));

  const handleColumnsChange = React.useCallback(
      newColumns => {
        if (columnsKpiId) {
          onColumnsChange(
              newColumns,
              columnsKpiId,
              kpisWithMatchingColumns.map(kpi => kpi.get("id"))
          );
        }
      },
      [onColumnsChange, columnsKpiId, kpisWithMatchingColumns]);

  return (
      <Drawer
          open={open}
          onRequestClose={onClose}
          showCloseButton={true}
          title={kpiName}
          openRight={true}
          width={"80%"}
      >
        <KpiDrawerMetricTabs
            entityTypeToLabel={entityTypeToLabel}
            idToTemplate={idToTemplate}
            openedKpiIds={openedKpiIds}
            focusedKpiTabId={focusedTabKpiId}
            idToWrappedKpi={idToWrappedKpi}
            onChangeMetricTab={onChangeMetricTab}
            onCloseMetricTab={onCloseMetricTab}
            onOpenMetricTab={onOpenMetricTab}
            onAddMetricClick={onAddMetricClick} />
        <div ref={ref} css={drawerContentWithFooterStyle(theme)}>
          <TabbedKpiEditor
              drawerContentHeight={height}
              theme={theme}
              isEditor={isEditor}
              isCube19User={isCube19User}
              clientHasEdit={clientHasEdit}
              isLockedByOnboarding={isLockedByOnboarding}
              actionTypes={actionTypes}
              idToTemplate={idToTemplate}
              typeToGroupingEntity={typeToGroupingEntity}
              availableOwners={availableOwners}
              idToEntityColumn={idToEntityColumn}
              entityTypeToLabel={entityTypeToLabel}
              entityType={entityType}
              kpi={kpi}
              parentCombinedKpi={parentCombinedKpi}
              combinedKpi={combinedKpi}
              combineError={combineError}
              onKpiChange={handleKpiChange}
              kpiIsParent={kpiIsCombinationParent}
              columns={columns}
              submitError={submitError}
              onColumnsChange={handleColumnsChange}
              onUnmirror={onUnmirror}
              kpisWithMatchingRootEntity={kpisWithMatchingRootEntity}
              masterMetricsAvailableToCombineWith={masterMetricsAvailableToCombineWith}
              kpisThatShareColumnsWithThisKpi={kpisThatShareColumnsWithThisKpi}
              hasNewConfig={hasNewConfig}
              testResult={testResult}
              onTest={handleTest}
              testConfig={testConfig}
              onTestConfigChange={handleTestConfigChange}
              rootChanged={rootChanged}
              onDependencyClick={onNavigationClick}
              kpiIdToMasterKpis={kpiIdToMasterKpis}
              queryType={queryType}
              columnsQueryType={columnsQueryType}
              hasRelationships={hasRelationships}
              kpiIdToDependentKpis={kpiIdToDependentKpis}
              kpiIdToContributingKpis={kpiIdToContributingKpis}
              requiresExplanationUpdate={requiresExplanationUpdate}
              tabId={tabId}
              searchFocused={searchFocused}
              filterText={filterText}
              onChangeTab={onChangeTab}
              onClearRequiresExplanationUpdate={onClearRequiresExplanationUpdate}
              sumDoubleSplitsAvailable={sumDoubleSplitsAvailable}
              sumSplitAvailable={sumSplitAvailable}
              aggregationTypes={aggregationTypes}
              trendTypes={trendTypes}
              availableServerColumns={availableServerColumns}
              nameToCount={nameToCount}
          />
          <KpiActions
              kpi={kpi}
              isEditor={isEditor}
              isCube19User={isCube19User}
              clientHasEdit={clientHasEdit}
              handleKpiChange={handleKpiChange}
              onRevertToLegacyFormat={onRevertToLegacyFormat}
              onDisableMetricToggle={onDisableMetricToggle}
              onDeleteMetric={onDeleteMetric}
              handleDuplicateClick={handleDuplicateClick}
              handleInheritClick={handleInheritClick}
              inheritAndDuplicateDisabled={inheritAndDuplicateDisabled}
              legacyConfigMessage={legacyConfigMessage}
              hasNewConfig={hasNewConfig}
              variant="full"
          />
        </div>
        <div css={drawerControlsStyle(theme, "80%")}>
          <TextButton
              testId="open-unsaved-metrics-button"
              disabled={unOpenedChangedMetricsCount < 1}
              label={`Open Unsaved Metrics (${unOpenedChangedMetricsCount})`}
              onClick={() => onLoadUnsavedChangesClick()} />
          <div style={{display: "flex", flexGrow: 1}} />
          <TextButton
              testId="discard-all-changes-button"
              disabled={changedMetricsCount < 1}
              label="Discard Changes"
              onClick={() => onDiscardChanges()}
              css={discardButtonStyle(theme)} />
          <TextButton
              testId="save-all-metrics-button"
              disabled={changedMetricsCount < 1 || hasValidationErrors}
              type="primary"
              label={`Save All Metrics  (${changedMetricsCount})`}
              onClick={() => onSave()}
              css={Styles.applyButtonStyle} />
        </div>
      </Drawer>
  );
};

const drawerControlsStyle = (theme, width = "598px") => (
    css`
        display: flex;
        padding: 1.25rem;
        background: ${theme.palette.background.card};
        position: fixed;
        bottom: 0;
        width: ${width};
        z-index: 1;
        @media (max-width: 720px) {
            padding-left: 0;
            padding-right: 0;
            flex-direction: row;
        }
    `);

const discardButtonStyle = (theme) => css`
    background: #eee;
    margin: 0 10px;
    float: left;
    height: 42px;
    font-size: 15px;
`;

export default KpiDrawer;